import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  getAssignedServiceOrders,
  getCompletedAssignedServiceOrders,
  type ServiceOrderSummaryDto,
  type ServiceOrderSummaryPageDto,
} from '@/api/service-order.api'
import { resetStore } from '@/store/actions/reset'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

export const MY_COMPLETED_PAGE_SIZE = 50

interface MyOrdersState {
  active: ServiceOrderSummaryDto[]
  activeStatus: Status
  completed: ServiceOrderSummaryDto[]
  completedTotal: number
  completedPage: number
  completedStatus: Status
}

const initialState: MyOrdersState = {
  active: [],
  activeStatus: 'idle',
  completed: [],
  completedTotal: 0,
  completedPage: 0,
  completedStatus: 'idle',
}

export const fetchMyActiveOrders = createAsyncThunk<ServiceOrderSummaryDto[]>(
  'myOrders/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      return await getAssignedServiceOrders()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

/** Strona 1 zastępuje listę, kolejne dokładają się na jej koniec. */
export const fetchMyCompletedOrders = createAsyncThunk<ServiceOrderSummaryPageDto, number>(
  'myOrders/fetchCompleted',
  async (page, { rejectWithValue }) => {
    try {
      return await getCompletedAssignedServiceOrders({ page, limit: MY_COMPLETED_PAGE_SIZE })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const myOrdersSlice = createSlice({
  name: 'myOrders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyActiveOrders.pending, (state) => {
        state.activeStatus = 'loading'
      })
      .addCase(fetchMyActiveOrders.fulfilled, (state, action) => {
        state.activeStatus = 'succeeded'
        state.active = action.payload
      })
      .addCase(fetchMyActiveOrders.rejected, (state) => {
        state.activeStatus = 'failed'
      })
      .addCase(fetchMyCompletedOrders.pending, (state) => {
        state.completedStatus = 'loading'
      })
      .addCase(fetchMyCompletedOrders.fulfilled, (state, action) => {
        const { data, total, page } = action.payload
        state.completedStatus = 'succeeded'
        state.completed = page === 1 ? data : [...state.completed, ...data]
        state.completedTotal = total
        state.completedPage = page
      })
      .addCase(fetchMyCompletedOrders.rejected, (state) => {
        state.completedStatus = 'failed'
      })
      .addCase(resetStore, () => initialState)
  },
})

export default myOrdersSlice.reducer
